import { createSelector } from "reselect";

import { EntityType } from "@mapmycustomers/shared/enum";
import { CategorizedMapEntries } from "@mapmycustomers/shared/types/map";

import { RootState } from "@app/store/rootReducer";

import { getRouteEditorStopEntityIds } from "../../editor/selectors";

import { getRouteEntries, getRouteExternalEntries } from "./pins";

const routeRecordsListState = (state: RootState) => state.scene.route.routeDetails.map.recordsList;

const countMapEntries = (entries: CategorizedMapEntries): number =>
  [...entries.clusters, ...entries.entities, ...entries.multiPins].reduce(
    (sum, pin) => sum + pin.count,
    0
  );

export const getTotalRouteRecordsCount = createSelector(
  [getRouteEntries, getRouteExternalEntries],
  (entries, externalEntries) => countMapEntries(entries) + countMapEntries(externalEntries)
);

export const getRouteMultiPin = createSelector(
  routeRecordsListState,
  ({ multiPin }) => multiPin?.entity
);
export const getRouteMultiPinAddress = createSelector(
  routeRecordsListState,
  ({ multiPin }) => multiPin?.address
);

export const getRouteMultiPinRecords = createSelector(
  routeRecordsListState,
  ({ records }) => records
);
export const getRouteMultiPinRecordsCount = createSelector(
  routeRecordsListState,
  ({ totalRecords }) => totalRecords
);
export const getRouteMultiPinRecordsRange = createSelector(
  routeRecordsListState,
  ({ range }) => range
);
export const isRouteMultiPinRecordsLoading = createSelector(
  routeRecordsListState,
  ({ loading }) => loading
);

export const getRouteRecordsListSortOrder = createSelector(
  routeRecordsListState,
  ({ sort }) => sort
);
export const getRouteListViewLocationQuery = createSelector(
  routeRecordsListState,
  ({ query }) => query
);
export const getRouteRecordsListSearch = createSelector(
  routeRecordsListState,
  ({ search }) => search
);

// Create/Edit draw a hexagonal stop marker for every editor stop (incl. scheduled
// activities on Create), so that record's regular external pin must not render —
// it bleeds out from behind the hexagon (TART-28550). View keeps the unfiltered
// getRouteExternalEntries: its fetch already excludes route members server-side.
export const getRouteExternalEntriesWithoutStops = createSelector(
  [getRouteExternalEntries, getRouteEditorStopEntityIds],
  (external, { companyIds, personIds }): CategorizedMapEntries => {
    if (companyIds.length === 0 && personIds.length === 0) {
      return external; // referential stability when there is nothing to hide
    }
    const companies = new Set(companyIds);
    const people = new Set(personIds);
    return {
      ...external,
      entities: external.entities.filter(({ data }) =>
        data.entity === EntityType.COMPANY
          ? !companies.has(data.id)
          : data.entity === EntityType.PERSON
            ? !people.has(data.id)
            : true
      ),
    };
  }
);
